// Generat automat din README.md
import React, { useState } from 'react';
import { FileText, AlertTriangle, Clock, CheckCircle, Users, TrendingUp, Filter, Download, Eye, Plus, Search, MoreVertical, Calendar, Target, Activity, Shield } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const RaportareIncidente = () => {
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data pentru raportare incidente
  const incidentsData = {
    overview: {
      totalIncidents: 247,
      openIncidents: 23,
      criticalIncidents: 5,
      averageResolutionTime: '4.2h',
      incidentsThisMonth: 47,
      mttr: '3.8h'
    },
    incidents: [
      {
        id: 'INC-2024-001',
        title: 'Advanced Persistent Threat Detected',
        category: 'Security Breach',
        severity: 'Critical',
        status: 'In Progress',
        priority: 'P1',
        reportedBy: 'Automated Detection System',
        assignedTo: 'Security Team Lead',
        createdAt: '2024-03-15 14:32:15',
        updatedAt: '2024-03-15 15:45:22',
        affectedSystems: ['Web Server 01', 'Database Server 02', 'File Server 03'],
        description: 'Detectare activitate suspectă pe mai multe servere. Pattern consistent cu APT29 (Cozy Bear).',
        impact: 'High',
        businessImpact: 'Servicii web indisponibile pentru clienți. Risc de compromitere date confidențiale.',
        tags: ['APT', 'Data Breach', 'Critical Infrastructure'],
        attachments: ['forensic_log_001.log', 'network_traffic_analysis.pcap']
      },
      {
        id: 'INC-2024-002',
        title: 'Email Phishing Campaign',
        category: 'Phishing',
        severity: 'High',
        status: 'Resolved',
        priority: 'P2',
        reportedBy: 'Elena Vasilescu (Finance)',
        assignedTo: 'Security Analyst',
        createdAt: '2024-03-15 09:15:30',
        updatedAt: '2024-03-15 16:20:12',
        affectedSystems: ['Email Server', 'Finance Workstations'],
        description: 'Campanie phishing sofisticată țintind echipa financiară cu emailuri false.',
        impact: 'Medium',
        businessImpact: 'Risc financiar redus prin detectare rapidă. Nicio pierdere financiară.',
        tags: ['Phishing', 'CEO Fraud', 'Finance'],
        attachments: ['phishing_emails.eml', 'awareness_training_slides.pptx']
      },
      {
        id: 'INC-2024-003',
        title: 'DDoS Attack on Web Services',
        category: 'Availability',
        severity: 'High',
        status: 'Resolved',
        priority: 'P1',
        reportedBy: 'Monitoring System',
        assignedTo: 'Network Team',
        createdAt: '2024-03-14 22:18:45',
        updatedAt: '2024-03-15 02:45:33',
        affectedSystems: ['Web Server Cluster', 'Load Balancer', 'CDN'],
        description: 'Atac DDoS volumetric cu 15GB/s trafic malware către serviciile web principale.',
        impact: 'High',
        businessImpact: 'Indisponibilitate servicii web 4.5 ore. Pierdere estimată revenue: €12,000.',
        tags: ['DDoS', 'Availability', 'Web Services'],
        attachments: ['ddos_traffic_analysis.pdf', 'mitigation_report.docx']
      }
    ],
    complianceMetrics: {
      gdprIncidents: 12,
      iso27001Compliance: 96.5,
      reportingCompliance: 100,
      timeToReporting: '2.1h'
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'error';
      case 'In Progress': return 'warning';
      case 'Under Investigation': return 'info';
      case 'Resolved': return 'success';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'P1': return 'error';
      case 'P2': return 'warning';
      case 'P3': return 'info';
      case 'P4': return 'success';
      default: return 'default';
    }
  };

  const filteredIncidents = incidentsData.incidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = selectedSeverity === 'all' || incident.severity === selectedSeverity;
    const matchesStatus = selectedStatus === 'all' || incident.status === selectedStatus;
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const incidentColumns = [
    { 
      header: 'ID Incident', 
      key: 'id',
      render: (value, row) => (
        <div>
          <div className="font-mono text-sm font-medium">{value}</div>
          <div className="text-xs text-gray-500">{row.createdAt}</div>
        </div>
      )
    },
    { 
      header: 'Titlu', 
      key: 'title',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-xs text-gray-500">{row.category}</div>
        </div>
      )
    },
    { 
      header: 'Severitate', 
      key: 'severity',
      render: (value) => <Badge variant={getSeverityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Prioritate', 
      key: 'priority',
      render: (value) => <Badge variant={getPriorityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Asignat către', 
      key: 'assignedTo'
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <button
          onClick={() => {
            setSelectedIncident(row);
            setShowIncidentModal(true);
          }}
          className="text-primary-600 hover:text-primary-800 text-sm"
          title="Vezi Detalii"
        >
          <Eye className="w-4 h-4" />
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Raportare Incidente</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sistem complet de documentare și analiză a incidentelor de securitate cu compliance reporting
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            className="btn-secondary"
            onClick={() => setShowReportModal(true)}
          >
            <Download className="w-4 h-4 mr-2" />
            Generate Report
          </button>
          <button className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            New Incident
          </button>
        </div>
      </div>

      {/* Overview Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <Card title="Total Incidente" icon={FileText}>
          <div className="text-2xl font-bold text-primary-600">{incidentsData.overview.totalIncidents}</div>
          <div className="text-sm text-blue-600">Documentate complet</div>
        </Card>
        <Card title="Incidente Active" icon={AlertTriangle}>
          <div className="text-2xl font-bold text-red-600">{incidentsData.overview.openIncidents}</div>
          <div className="text-sm text-red-600">Necesită atenție</div>
        </Card>
        <Card title="Critical P1" icon={Shield}>
          <div className="text-2xl font-bold text-red-600">{incidentsData.overview.criticalIncidents}</div>
          <div className="text-sm text-red-600">Escalare imediată</div>
        </Card>
        <Card title="MTTR Mediu" icon={Clock}>
          <div className="text-2xl font-bold text-primary-600">{incidentsData.overview.mttr}</div>
          <div className="text-sm text-green-600">Sub target 6h</div>
        </Card>
        <Card title="Această Lună" icon={Calendar}>
          <div className="text-2xl font-bold text-primary-600">{incidentsData.overview.incidentsThisMonth}</div>
          <div className="text-sm text-yellow-600">+15% vs luna trecută</div>
        </Card>
        <Card title="Avg Resolution" icon={CheckCircle}>
          <div className="text-2xl font-bold text-primary-600">{incidentsData.overview.averageResolutionTime}</div>
          <div className="text-sm text-green-600">Îmbunătățit 12%</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Incidents Table */}
        <div className="lg:col-span-3">
          <Card title="Incidente de Securitate" subtitle="Documentare și tracking complet">
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Caută incident sau ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select 
                value={selectedSeverity} 
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Toate severitățile</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="all">Toate statusurile</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
              <button className="btn-secondary">
                <Filter className="w-4 h-4 mr-2" />
                Advanced
              </button>
            </div>
            <Table columns={incidentColumns} data={filteredIncidents} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Compliance Metrics" icon={Shield}>
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                  ISO 27001 Compliance
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${incidentsData.complianceMetrics.iso27001Compliance}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">
                    {incidentsData.complianceMetrics.iso27001Compliance}%
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                  <div className="font-medium text-green-800 dark:text-green-200">GDPR Incidents</div>
                  <div className="text-lg font-bold text-green-600">{incidentsData.complianceMetrics.gdprIncidents}</div>
                </div>
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <div className="font-medium text-purple-800 dark:text-purple-200">Time to Report</div>
                  <div className="text-lg font-bold text-purple-600">{incidentsData.complianceMetrics.timeToReporting}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Incident Detail Modal */}
      <Modal
        isOpen={showIncidentModal}
        onClose={() => {
          setShowIncidentModal(false);
          setSelectedIncident(null);
        }}
        title={`Incident: ${selectedIncident?.id}`}
        size="large"
      >
        {selectedIncident && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span className="font-mono">{selectedIncident.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Categorie:</span>
                    <span>{selectedIncident.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Severitate:</span>
                    <Badge variant={getSeverityColor(selectedIncident.severity)}>{selectedIncident.severity}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <Badge variant={getStatusColor(selectedIncident.status)}>{selectedIncident.status}</Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Business Impact</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <strong>Impact:</strong> {selectedIncident.impact}<br/>
                  <strong>Descriere:</strong> {selectedIncident.businessImpact}
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {selectedIncident.description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Sisteme Afectate</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedIncident.affectedSystems.map((system, index) => (
                    <Badge key={index} variant="error" className="text-xs">
                      {system}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedIncident.tags.map((tag, index) => (
                    <Badge key={index} variant="info" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Attachments</h3>
              <div className="flex flex-wrap gap-2">
                {selectedIncident.attachments.map((file, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span>{file}</span>
                    <button className="text-blue-600 hover:text-blue-800">
                      <Download className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Export PDF</button>
              <button className="btn-primary">Add Comment</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Report Generation Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Generate Incident Report"
        size="large"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Report Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Report Type
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>Executive Summary</option>
                  <option>Detailed Technical Report</option>
                  <option>Compliance Report (ISO 27001)</option>
                  <option>GDPR Incident Report</option>
                  <option>Monthly Trend Analysis</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Period
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                  <option>Last 30 days</option>
                  <option>Last 90 days</option>
                  <option>Current Quarter</option>
                  <option>Current Year</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Include Sections</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm">Executive Summary</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm">Incident Statistics</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm">Severity Analysis</span>
                </label>
              </div>
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm">Response Time Metrics</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" defaultChecked />
                  <span className="text-sm">Compliance Metrics</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">Recommendations</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowReportModal(false)}
            >
              Cancel
            </button>
            <button className="btn-primary">
              <Download className="w-4 h-4 mr-2" />
              Generate Report
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RaportareIncidente;